// Global (cross-company) access Schedule library — list/create/rename/
// delete Schedules and manage each one's weekly access spans. Replaces the
// per-company AccessSpansEditor embedded in CompanyFormPage (Task 7 of
// docs/plans/2026-08-09-global-access-schedules.md) — companies now assign
// zero or more of these Schedules instead of owning their own spans.
// Mirrors the list-page skeleton (PageHeader -> loading/error/empty/data,
// LayerCard + Table, toast on mutation errors) and the create/edit-Dialog +
// ConfirmDialog-delete idiom from
// src/components/access-readers/access-readers-section.tsx. Per-schedule
// span editing reuses the shared SpanTableEditor (Task 7 extraction).
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  Banner,
  Breadcrumbs,
  Button,
  Dialog,
  Empty,
  Input,
  Label,
  LayerCard,
  Loader,
  Table,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import {
  ClockIcon,
  HouseIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react'
import { ApiError } from '../api/client'
import {
  listSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  getScheduleSpans,
  putScheduleSpans,
  type Schedule,
} from '../api/schedules'
import {
  SpanTableEditor,
  emptySpanRow,
  spanDtoToRow,
  spanRowToDto,
  validateSpanRows,
  type SpanRow,
  type DayField,
} from '../components/access-spans/span-table-editor'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useTranslation } from '../i18n'

export function SchedulesPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [creating, setCreating] = useState(false)

  const [editTarget, setEditTarget] = useState<Schedule | null>(null)
  const [editName, setEditName] = useState('')
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<Schedule | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [spansTarget, setSpansTarget] = useState<Schedule | null>(null)
  const [spanRows, setSpanRows] = useState<SpanRow[]>([])
  const [spansLoading, setSpansLoading] = useState(false)
  const [spansError, setSpansError] = useState<string | null>(null)
  const [spansSaving, setSpansSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      setSchedules(await listSchedules(token))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently, t])

  useEffect(() => {
    void load()
  }, [load])

  function reportError(err: unknown) {
    toasts.add({
      variant: 'error',
      title: t('common.errorTitle'),
      description: err instanceof ApiError ? err.message : t('common.error'),
    })
  }

  function openCreate() {
    setCreateName('')
    setCreateOpen(true)
  }

  async function handleCreateSubmit(e: FormEvent) {
    e.preventDefault()
    setCreating(true)
    try {
      const token = await getAccessTokenSilently()
      const created = await createSchedule(createName, token)
      setSchedules((prev) => [...prev, created])
      setCreateOpen(false)
      toasts.add({ variant: 'success', title: t('schedules.createSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setCreating(false)
    }
  }

  function openEdit(schedule: Schedule) {
    setEditTarget(schedule)
    setEditName(schedule.name)
  }

  async function handleEditSubmit(e: FormEvent) {
    e.preventDefault()
    if (!editTarget) return
    setSaving(true)
    try {
      const token = await getAccessTokenSilently()
      const updated = await updateSchedule(editTarget.id, editName, token)
      setSchedules((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))
      setEditTarget(null)
      if (spansTarget?.id === updated.id) setSpansTarget(updated)
      toasts.add({ variant: 'success', title: t('schedules.updateSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(schedule: Schedule) {
    setDeleting(true)
    try {
      const token = await getAccessTokenSilently()
      await deleteSchedule(schedule.id, token)
      setSchedules((prev) => prev.filter((s) => s.id !== schedule.id))
      setDeleteTarget(null)
      if (spansTarget?.id === schedule.id) setSpansTarget(null)
      toasts.add({ variant: 'success', title: t('schedules.deleteSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setDeleting(false)
    }
  }

  async function openSpans(schedule: Schedule) {
    setSpansTarget(schedule)
    setSpansLoading(true)
    setSpansError(null)
    try {
      const token = await getAccessTokenSilently()
      const { spans } = await getScheduleSpans(schedule.id, token)
      setSpanRows(spans.map((span) => spanDtoToRow(span)))
    } catch (err) {
      setSpansError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setSpansLoading(false)
    }
  }

  function addSpanRow() {
    setSpanRows((prev) => [...prev, emptySpanRow()])
  }

  function removeSpanRow(index: number) {
    setSpanRows((prev) => prev.filter((_, i) => i !== index))
  }

  function updateSpanRow(index: number, patch: Partial<SpanRow>) {
    setSpanRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  function toggleSpanDay(index: number, day: DayField) {
    setSpanRows((prev) => prev.map((row, i) => (i === index ? { ...row, [day]: !row[day] } : row)))
  }

  async function handleSaveSpans() {
    if (!spansTarget) return
    const validationError = validateSpanRows(spanRows, t)
    if (validationError) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: validationError,
      })
      return
    }

    setSpansSaving(true)
    try {
      const token = await getAccessTokenSilently()
      const { spans } = await putScheduleSpans(spansTarget.id, spanRows.map(spanRowToDto), token)
      setSpanRows(spans.map((span) => spanDtoToRow(span)))
      toasts.add({ variant: 'success', title: t('schedules.spans.saveSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setSpansSaving(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('schedules.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('schedules.title')}
        description={t('schedules.description')}
      >
        <Button variant="primary" size="sm" icon={<PlusIcon size={16} />} onClick={openCreate}>
          {t('schedules.addSchedule')}
        </Button>
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && schedules.length === 0 && (
        <Empty
          icon={<ClockIcon size={48} className="text-kumo-inactive" />}
          title={t('schedules.empty')}
        />
      )}

      {!loading && !error && schedules.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('schedules.name')}</Table.Head>
                    <Table.Head>{t('schedules.actions')}</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {schedules.map((schedule) => (
                    <Table.Row key={schedule.id} data-testid={`schedule-row-${schedule.id}`}>
                      <Table.Cell>{schedule.name}</Table.Cell>
                      <Table.Cell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<ClockIcon size={16} />}
                            aria-label={t('schedules.manageSpans')}
                            onClick={() => void openSpans(schedule)}
                          >
                            {t('schedules.manageSpans')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PencilSimpleIcon size={16} />}
                            aria-label={t('schedules.rename')}
                            onClick={() => openEdit(schedule)}
                          />
                          <Button
                            variant="secondary-destructive"
                            size="sm"
                            icon={<TrashIcon size={16} />}
                            aria-label={t('schedules.delete')}
                            onClick={() => setDeleteTarget(schedule)}
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}

      {createOpen && (
        <Dialog.Root open={createOpen} onOpenChange={setCreateOpen}>
          <Dialog size="sm" className="p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <Dialog.Title className="text-lg font-semibold">
                {t('schedules.create.title')}
              </Dialog.Title>
              <Dialog.Close
                render={(props) => (
                  <Button
                    {...props}
                    variant="secondary"
                    shape="square"
                    size="sm"
                    icon={<XIcon size={16} />}
                    aria-label={t('common.close')}
                  />
                )}
              />
            </div>
            <form onSubmit={(e) => void handleCreateSubmit(e)} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Label>{t('schedules.form.name')}</Label>
                <Input
                  aria-label={t('schedules.form.name')}
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => setCreateOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={creating}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Dialog>
        </Dialog.Root>
      )}

      {editTarget && (
        <Dialog.Root
          open={Boolean(editTarget)}
          onOpenChange={(open) => {
            if (!open) setEditTarget(null)
          }}
        >
          <Dialog size="sm" className="p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <Dialog.Title className="text-lg font-semibold">
                {t('schedules.edit.title')}
              </Dialog.Title>
              <Dialog.Close
                render={(props) => (
                  <Button
                    {...props}
                    variant="secondary"
                    shape="square"
                    size="sm"
                    icon={<XIcon size={16} />}
                    aria-label={t('common.close')}
                  />
                )}
              />
            </div>
            <form onSubmit={(e) => void handleEditSubmit(e)} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Label>{t('schedules.form.name')}</Label>
                <Input
                  aria-label={t('schedules.form.name')}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => setEditTarget(null)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={saving}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Dialog>
        </Dialog.Root>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title={t('schedules.deleteConfirm.title', { name: deleteTarget?.name ?? '' })}
        description={t('schedules.deleteConfirm.description')}
        confirmLabel={t('schedules.delete')}
        variant="destructive"
        loading={deleting}
        onConfirm={() => (deleteTarget ? handleDelete(deleteTarget) : undefined)}
      />

      {spansTarget && (
        <LayerCard className="mt-4">
          <LayerCard.Primary>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <Text as="h2" variant="heading2">
                  {t('schedules.spans.titleFor', { name: spansTarget.name })}
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<PlusIcon size={16} />}
                  onClick={addSpanRow}
                >
                  {t('accessSpans.add')}
                </Button>
              </div>

              {spansLoading && (
                <div className="flex items-center justify-center py-16">
                  <Loader size="lg" />
                </div>
              )}

              {!spansLoading && spansError && (
                <Banner variant="error">
                  {t('common.errorPrefix')} {spansError}
                </Banner>
              )}

              {!spansLoading && !spansError && spanRows.length === 0 && (
                <Empty
                  icon={<ClockIcon size={48} className="text-kumo-inactive" />}
                  title={t('accessSpans.empty')}
                />
              )}

              {!spansLoading && !spansError && spanRows.length > 0 && (
                <>
                  <SpanTableEditor
                    rows={spanRows}
                    onRemoveRow={removeSpanRow}
                    onUpdateRow={updateSpanRow}
                    onToggleDay={toggleSpanDay}
                  />
                  <div className="flex justify-end pt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      loading={spansSaving}
                      onClick={() => void handleSaveSpans()}
                    >
                      {t('accessSpans.save')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}

SchedulesPage.displayName = 'SchedulesPage'
